import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "./vfs_fonts";

import moment from "moment";
import "moment/locale/th";
import { convertNumberToThaiBahtText } from "../../../utils/numberFormatter";
import { formatNumber } from "../../../utils/formatNumber";
import { getOrderedDeductionEntries } from "./deductionOrder";
import { buildCarbonSummary } from "./carbonSummary";

let pdfReadyPromise = null;

function setupFonts() {
  if (!pdfMake.vfs || Object.keys(pdfMake.vfs).length === 0) {
    const vfs = pdfFonts?.pdfMake?.vfs || pdfFonts?.default?.pdfMake?.vfs;
    if (vfs) pdfMake.vfs = vfs;
  }

  pdfMake.fonts = {
    THSarabunNew: {
      normal: "THSarabunNew.ttf",
      bold: "THSarabunNew_Bold.ttf",
      italics: "THSarabunNew_Italic.ttf",
      bolditalics: "THSarabunNew_BoldItalic.ttf",
    },
  };
}

async function ensurePdfMakeReady() {
  if (!pdfReadyPromise) {
    pdfReadyPromise = (async () => {
      setupFonts();

      await Promise.resolve();

      if (!pdfMake.vfs || Object.keys(pdfMake.vfs).length === 0) {
        throw new Error("pdfMake vfs not loaded");
      }
      return true;
    })();
  }
  return pdfReadyPromise;
}
async function getBase64ImageFromUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ---------- ใช้ราคาต่อหน่วยและยอดของรายการจริงจาก transaction ----------
// ไม่กระจายส่วนต่างของยอดรวมกลับไปเปลี่ยนราคาสินค้าแต่ละรายการ
function buildInvoiceRows(items = [], transactionBreakdown = []) {
  const N = (v) => Number(v ?? 0) || 0;

  return items.map((it, index) => {
    const breakdown = transactionBreakdown?.[index];
    const weight = N(breakdown?.usedWeight ?? it.netWeight);
    const total = N(breakdown?.totalPrice ?? it.netPrice);

    return {
      productName: breakdown?.productName ?? it.productName,
      weight,
      pricePerUnit: N(
        weight !== 0
          ? total / weight
          : (it.pricePerUnit ?? 0),
      ),
      total,
    };
  });
}

export async function generateInvoicePdf(
  transaction,
  method,
  transactionBreakdown = [],
) {
  await ensurePdfMakeReady();
  const safe = (v, d = "") => (v == null ? d : v);
  const N = (v) => Number(v ?? 0) || 0;
  const thBuddhistDateTime = (d) => {
    if (!d) return "-";
    const m = moment(d).locale("th");
    return `${m.format("dddd DD/MM/")}${m.year() + 543} ${m.format("HH:mm")}`;
  };

  const items = Array.isArray(transaction?.items) ? transaction?.items : [];
  const driverImg = transaction.driver?.img
    ? await getBase64ImageFromUrl(transaction.driver.img)
    : null;
  const customerDisplayName = transaction?.customer?.name || "";

  // -------- รวมจากรายการต้นฉบับใน transaction --------
  const totalWeight = items.reduce((s, it) => s + N(it.weight), 0);
  const totalDeductWeight = items.reduce((s, it) => s + N(it.deductWeight), 0);
  const totalNetWeight = items.reduce((s, it) => s + N(it.netWeight), 0);

  // -------- สิ่งเจือปน / หักสินค้าอื่น --------
  const summarizeImpurities = (it) => {
    const parts = [];
    let total = 0;
    for (const { kind, detail } of getOrderedDeductionEntries(it)) {
      const w = N(kind === "deductedItem" ? detail?.weight2 ?? detail?.weight : detail?.weight);
      total += w;
      const label =
        kind === "deductedItem"
          ? detail?.productName || "สินค้าอื่น"
          : detail?.type || "ไม่ระบุ";
      parts.push(`${formatNumber(w)} (${label})`);
    }
    return { total, breakdown: parts };
  };

  const totalImpuritiesAll = items.reduce((sum, it) => {
    const { total } = summarizeImpurities(it);
    return sum + N(total);
  }, 0);

  const adjustedTotalWeight = Math.max(
    0,
    N(totalWeight) - N(totalImpuritiesAll)
  );

  const adjustedRows = buildInvoiceRows(items, transactionBreakdown);

  // ใช้ยอดสุทธิเดียวกับหน้า Buy; fallback เฉพาะกรณี transaction ยังไม่มียอด
  const finalTicketTotal =
    N(transaction?.totalNetPrice) ||
    adjustedRows.reduce((s, r) => s + N(r.total), 0);
  const carbonSummary = buildCarbonSummary(transaction);

  // ------------------------------------------------
  //                HEADER (เหมือนเดิม)
  // ------------------------------------------------
  const headerFn = () => ({
    margin: [28, 18, 28, 6],
    stack: [
      {
        columns: [
          {
            // ฝั่งซ้าย
            width: "30%",
            stack: [
              { text: "ใบรับสินค้า", bold: true, fontSize: 16, noWrap: false },

              {
                text: [
                  { text: "เลขที่ตั๋ว: ", bold: true },
                  { text: String(safe(transaction?.billNumber, "-")) },
                ],
                noWrap: false,
              },

              {
                text: `${safe(customerDisplayName, "")}`,
                bold: true,
                noWrap: false,
              },

              {
                text: transaction?.driver?.name
                  ? `ผู้ส่ง/ผู้ขายสินค้า: ${transaction?.driver?.name} ${transaction?.driver?.surname}`
                  : "\u00A0",
                noWrap: false,
              },
              {
                text:
                  (transaction?.customer?.address
                    ? transaction?.customer.address
                    : "\u00A0") +
                  (transaction?.customer?.phone
                    ? ` , โทร ${transaction?.customer?.phone}`
                    : ""),
                noWrap: false,
              },
              {
                text: transaction?.car
                  ? `${transaction?.car?.type} ${transaction?.car?.province} ${transaction?.car?.license}`
                  : "\u00A0",
                noWrap: false,
              },
            ],
          },
          {
            // ฝั่งขวา
            width: "*",
            stack: [
              {
                text: transaction?.branch?.companyName || "",
                alignment: "right",
                bold: true,
              },
              {
                text: transaction?.branch?.address || "",
                alignment: "right",
              },
              {
                text:
                  transaction?.branch?.phoneNumber &&
                  `โทร ${transaction?.branch?.phoneNumber}`,
                alignment: "right",
              },
              {
                text:
                  transaction?.branch?.taxNumber &&
                  `เลขประจำตัวผู้เสียภาษี ${transaction?.branch?.taxNumber}`,
                alignment: "right",
              },
              {
                text: [
                  { text: "วันที่เข้า ", bold: true },
                  { text: thBuddhistDateTime(transaction?.createdAt) },
                ],
                alignment: "right",
              },
              {
                text: [
                  { text: "วันที่รับเงิน ", bold: true },
                  {
                    text: transaction?.paidAt
                      ? thBuddhistDateTime(transaction?.paidAt)
                      : "-",
                  },
                ],
                alignment: "right",
              },
            ],
          },
        ],
        columnGap: 20,
      },

      // เส้นคั่นใต้หัวบิล
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 540, y2: 0, lineWidth: 1 }],
      },
    ],
  });

  // ------------------------------------------------
  //                ตารางรายการสินค้า
  // ------------------------------------------------
  const itemRows = adjustedRows.map((row, idx) => {
    // console.log("row",row)
    const originalItem = items[idx];
    const { total: impTotal, breakdown } = summarizeImpurities(originalItem);
    const impurityStr =
      impTotal > 0 && breakdown.length > 0
        ? `เจือปน: ${formatNumber(impTotal)} -> ${breakdown.join(", ")}`
        : "";

    const lineNetWeight = N(row.weight);
    const linePricePerUnit = N(row.pricePerUnit);
    const lineTotal = N(row.total);

    const mainLine = {
      table: {
        widths: ["*", 60, 70, 80],
        body: [
          [
            { text: `${idx + 1}. ${safe(row.productName, "-")}` },
            { text: formatNumber(lineNetWeight), alignment: "right" },
            { text: formatNumber(linePricePerUnit), alignment: "right" },
            { text: formatNumber(lineTotal), alignment: "right" },
          ],
        ],
      },
      layout: "noBorders",
    };

    const detailLine = {
      table: {
        widths: [60, 60, "*"],
        body: [
          [
            { text: `รวม: ${formatNumber(N(originalItem.weight))}` },
            { text: `หัก: ${formatNumber(N(originalItem.deductWeight))}` },
            { text: impurityStr, noWrap: false },
          ],
        ],
      },
      layout: "noBorders",
      margin: [0, -6, 0, 0],
    };
    // const detailLine2 = {
    //   columns: [
    //     { width: "*", text: "" }, // เว้นซ้าย
    //     {
    //       table: {
    //         widths: [100],
    //         body: [[{ text: `สินค้านี้ใช้ราคา PO#001`, alignment: "center" }]],
    //       },
    //       layout: "noBorders",
    //     },
    //     { width: "*", text: "" }, // เว้นขวา
    //   ],
    // };

    return [
      {
        colSpan: 4,
        stack: [mainLine, detailLine],
        unbreakable: true,
      },
      {},
      {},
      {},
    ];
  });

  const itemsTable = {
    table: {
      headerRows: 1,
      widths: ["*", 60, 70, 80],
      body: [
        // แถวหัวตาราง
        [
          { text: `รายการ (${items.length})`, bold: true, fontSize: 14 },
          { text: "สุทธิ", bold: true, alignment: "right", fontSize: 14 },
          { text: "ราคา/หน่วย", bold: true, alignment: "right" },
          { text: "รวม", bold: true, alignment: "right", fontSize: 14 },
        ],
        // ตามด้วยแถวรายการจริง
        ...itemRows,
      ],
    },
    layout: {
      hLineWidth: () => 1,
      vLineWidth: () => 0,
      hLineColor: () => "#000",
      paddingTop: () => -2,
      paddingBottom: () => -2,
    },
  };

  // ------------------------------------------------
  //          VAT / GRAND TOTAL (ส่วนท้าย)
  // ------------------------------------------------
  const VAT_RATE = 0.07;
  const taxOption = transaction?.customer?.purchaseTaxOption; // 'vatExcluded' | 'vatIncluded' | อื่นๆ = ไม่มี VAT

  // baseAmountRaw คือยอดก่อน VAT
  // - ถ้าราคารวมเป็นแบบ exclude VAT => grand = base + vat
  // - ถ้า include VAT => แยก vat ออก
  // - ถ้าไม่มี VAT => beforeVat = grand = baseAmountRaw
  const baseAmountRaw = finalTicketTotal;

  let beforeVat, vatAmt, grand;
  if (taxOption === "vatExcluded") {
    beforeVat = baseAmountRaw;
    vatAmt = beforeVat * VAT_RATE;
    grand = beforeVat + vatAmt;
  } else if (taxOption === "vatIncluded") {
    grand = baseAmountRaw;
    vatAmt = (grand * VAT_RATE) / (1 + VAT_RATE);
    beforeVat = grand - vatAmt;
  } else {
    beforeVat = baseAmountRaw;
    vatAmt = 0;
    grand = beforeVat;
  }

  // ------------------------------------------------
  //                SUMMARY + SIGNATURE
  // ------------------------------------------------
  const summaryAndSign = {
    unbreakable: true,
    stack: [
      // 1) สรุปน้ำหนักสินค้าสุทธิ
      {
        table: {
          widths: ["*", 60, 70, 80],
          body: [
            [
              {
                stack: [
                  { text: `น้ำหนักสินค้าสุทธิ` },
                  {
                    table: {
                      widths: [60, 60, "*"],
                      body: [
                        [
                          {
                            text: `รวม: ${formatNumber(adjustedTotalWeight)}`,
                          },
                          {
                            text: `หัก: ${formatNumber(totalDeductWeight)}`,
                          },
                          {
                            text: `เจือปน: ${formatNumber(totalImpuritiesAll)}`,
                          },
                        ],
                      ],
                    },
                    layout: "noBorders",
                    margin: [0, -5, 0, 0],
                  },
                ],
              },
              { text: formatNumber(totalNetWeight), alignment: "right" },
              { text: "" },
              { text: "" },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingTop: () => 2,
          paddingBottom: () => 0,
        },
        margin: [50, 0, 0, 0],
      },
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 490, y2: 0, lineWidth: 1 }],
        margin: [50, 0, 0, 0],
      },

      // 2) น้ำหนักรถขนส่ง
      {
        table: {
          widths: ["*", 60, 70, 80],
          body: [
            [
              {
                stack: [
                  { text: `น้ำหนักรถขนส่ง` },
                  {
                    table: {
                      widths: [60, 60, "*"],
                      body: [
                        [
                          {
                            text: `รวม: ${formatNumber(
                              transaction?.carHeavyWeight
                            )}`,
                          },
                          {
                            text: `หัก: ${formatNumber(
                              transaction?.carLightWeight
                            )}`,
                          },
                          { text: "" },
                        ],
                      ],
                    },
                    layout: "noBorders",
                    margin: [0, -5, 0, 0],
                  },
                ],
              },
              {
                text: formatNumber(
                  N(transaction?.carHeavyWeight) -
                  N(transaction?.carLightWeight)
                ),
                alignment: "right",
              },
              { text: "" },
              { text: "" },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingTop: () => 2,
          paddingBottom: () => 0,
        },
        margin: [50, 0, 0, 0],
      },
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 540, y2: 0, lineWidth: 1 }],
      },

      // 3) ชั่งโดย / สั่งจ่ายโดย
      {
        columns: [
          {
            width: "65%",
            text: [
              { text: "ชั่งโดย: ", bold: true },
              { text: `${safe(transaction?.weighedByNames, "-")}` },
            ],
          },
          {
            width: "35%",
            text: [
              { text: "สั่งจ่ายโดย: ", bold: true },
              { text: `${safe(transaction?.paidByNames, "-")}` },
            ],
            alignment: "right",
          },
        ],
      },
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 540, y2: 0, lineWidth: 1 }],
      },

      // 4) ประเภทการชำระเงิน + ถ้าโอนให้โชว์ธนาคาร / ถ้า ATM ให้โชว์ QR
      {
        columns: [
          {
            width: "65%",
            text: [
              { text: "ประเภทการชำระเงิน: ", bold: true },
              { text: `${safe(method, "")}` },
            ],
          },
          {
            width: "35%",
            stack: (() => {
              if (method === "โอนเงิน") {
                return [
                  {
                    stack: [
                      {
                        text: [
                          { text: "ธนาคาร: ", bold: true },
                          {
                            text: `${safe(
                              transaction?.customer?.bankName,
                              "ไม่ได้ระบุ"
                            )}`,
                          },
                        ],
                      },
                      {
                        text: [
                          { text: "สาขา: ", bold: true },
                          {
                            text: `${safe(
                              transaction?.customer?.bankBranch,
                              "ไม่ได้ระบุ"
                            )}`,
                          },
                        ],
                      },
                      {
                        text: [
                          { text: "เลขบัญชี: ", bold: true },
                          {
                            text: `${safe(
                              transaction?.customer?.bankAccountNumber,
                              "ไม่ได้ระบุ"
                            )}`,
                          },
                        ],
                      },
                      {
                        text: [
                          { text: "ชื่อบัญชี: ", bold: true },
                          {
                            text: `${safe(
                              transaction?.customer?.bankAccountName,
                              "ไม่ได้ระบุ"
                            )}`,
                          },
                        ],
                      },
                    ],
                    alignment: "left",
                    unbreakable: true,
                  },
                ];
              } else if (method === "ATM") {
                return [
                  {
                    stack: [
                      {
                        qr: safe("123456789", ""),
                        fit: 100,
                      },
                    ],
                    margin: [10, 10, 10, 10],
                  },
                ];
              }
              return [];
            })(),
          },
        ],
      },

      // เส้นคั่นคู่
      {
        stack: [
          {
            canvas: [
              { type: "line", x1: 0, y1: 0, x2: 540, y2: 0, lineWidth: 2 },
            ],
          },
          {
            canvas: [
              { type: "line", x1: 0, y1: 3, x2: 540, y2: 3, lineWidth: 1 },
            ],
          },
        ],
        margin: [0, 0, 0, 5],
      },

      // 5) กล่องยอดรวมขวา — ใช้ยอดหลังปรับ (grand ซึ่งรวม VAT ถ้ามี)
      {
        columns: [
          { width: "55%", text: "" },
          {
            width: "45%",
            table: {
              widths: ["*", "auto"],
              body: [
                [
                  { text: "ยอดรวมก่อนเสียภาษี", alignment: "right" },
                  { text: formatNumber(beforeVat), alignment: "right" },
                ],
                [
                  { text: "ภาษีมูลค่าเพิ่ม (VAT 7%)", alignment: "right" },
                  { text: formatNumber(vatAmt), alignment: "right" },
                ],
                [
                  { text: "ยอดรวม", alignment: "right", bold: true },
                  {
                    text: formatNumber(grand),
                    alignment: "right",
                    bold: true,
                  },
                ],
              ],
            },
            layout: {
              hLineWidth: () => 0,
              vLineWidth: () => 0,
              paddingTop: () => 0,
              paddingBottom: () => 0,
              paddingLeft: () => 0,
              paddingRight: () => 5,
            },
          },
        ],
      },

      // 6) จำนวนเงินตัวหนังสือ
      {
        text: `(${convertNumberToThaiBahtText(grand)})`,
        alignment: "left",
        margin: [0, 0, 0, 50],
      },

      // 7) ลายเซ็น
      {
        columns: [
          {
            width: "50%",
            stack: [
              {
                text: "ลงชื่อ : ____________________________(ผู้รับเงิน/ผู้ส่งของ)",
                alignment: "center",
              },
              {
                text: `${transaction?.driver?.name ?? ""} ${transaction?.driver?.surname ?? ""
                  }`,
                alignment: "center",
                bold: true,
                margin: [0, 0, 40, 6],
              },
            ],
          },
          {
            width: "50%",
            stack: [
              {
                text: "ลงชื่อ : ____________________________(ผู้จ่าย)",
                alignment: "center",
              },
            ],
          },
        ],
      },
      {
        columns: [
          {
            width: "50%",
            columns: [
              {
                width: 65,
                ...(driverImg
                  ? { image: driverImg, fit: [60, 60] }
                  : { text: " ", width: 65 }),
              },
              {
                width: "*",

                stack: [
                  {
                    text: `เลขบัตรประชาชน: ${transaction?.driver?.idCard || "-"}`,
                    fontSize: 12,
                    margin: [5, 0, 0, 2],
                  },
                  {
                    text: ` บัตรหมดอายุ: ${transaction?.driver?.expire || "-"}`,
                    fontSize: 12,
                    margin: [5, 0, 0, 2],
                  },
                  {
                    text: `เบอร์โทร: ${transaction?.driver?.phone || "-"}`,
                    fontSize: 12,
                    margin: [5, 0, 0, 0],
                  },
                ],
                fontSize: 12,
                margin: [5, 0, 0, 0],
              },
            ],
          },
        ],
      },
    ],
  };

  // ------------------------------------------------
  //                 FOOTER
  // ------------------------------------------------
  const footer = (currentPage, pageCount) => ({
    margin: [28, 6, 28, 12],
    stack: [
      ...(currentPage === pageCount && carbonSummary ? [carbonSummary] : []),
      {
        canvas: [{ type: "line", x1: 0, y1: 0, x2: 540, y2: 0, lineWidth: 1 }],
      },
      {
        table: {
          widths: ["90%", "10%"],
          body: [
            [
              {
                text: "ข้าพเจ้า(ผู้ขาย) ขอรับรองว่าทรัพย์สินที่นำมาขายทั้งหมดนี้เป็นกรรมสิทธิ์ของข้าพเจ้าอย่างถูกต้องตามกฎหมาย",
                alignment: "left",
              },
              { text: `หน้า ${currentPage}/${pageCount}`, alignment: "right" },
            ],
          ],
        },
        layout: "noBorders",
      },
    ],
  });

  // ------------------------------------------------
  //                 สร้าง PDF
  // ------------------------------------------------
  const docDefinition = {
    pageSize: "A4",
    pageMargins: [28, 145, 28, Number(transaction?.shopId) === 315 ? 140 : 32],
    header: headerFn,
    footer,
    content: [itemsTable, summaryAndSign],
    defaultStyle: { font: "THSarabunNew", fontSize: 13 },
  };

  pdfMake.createPdf(docDefinition).open();
}
